'use client';

import { Grid, Typography } from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { adaptGridValueCallback } from '@parkchirp/ui-library';
import React, { FC, useMemo } from 'react';
import { MethodOfAcceptingTermsAndConditions } from '../../enums';
import { SetShowDialog } from '../../types';
import { dayjs } from '../../utils';
import { CenteredDismissModal } from '../modals';

type TermsUser = {
	firstName?: string;
	lastName?: string;
	email?: string;
} | null;

type TermsAcceptanceRow = {
	acceptanceDate?: string;
	methodOfAcceptance?: string;
	user?: TermsUser;
	termsAndConditionsHistory?: {
		id?: string;
		summary?: string;
		text?: string;
	};
};

type AchAuthorizationAcceptanceRow = {
	id: number;
	authorizationType?: string;
	methodOfAcceptance?: string;
	authorizationText?: string;
	operatorLegalName?: string;
	operatorAccountLast4?: string;
	operatorAbaLast4?: string;
	accountType?: string;
	amount?: number | string;
	effectiveDate?: string;
	recurrenceFrequency?: string;
	cancellationMethod?: string;
	revocationNoticeDays?: number;
	acceptedAt?: string;
	userAgent?: string;
	userIp?: string;
	user?: TermsUser;
};

const formatLabel = (value?: string) => {
	if (!value) {
		return 'N/A';
	}

	return value
		.replaceAll('_', ' ')
		.toLowerCase()
		.replace(/\b\w/g, (character) => character.toUpperCase());
};

type TermsAndConditionsProps = {
	subscriptionTermsAndConditionsAcceptance?: TermsAcceptanceRow[];
	achAuthorizationAcceptances?: AchAuthorizationAcceptanceRow[];
	hasAcceptedMostRecentTerms?: boolean;
	setShowDialog?: SetShowDialog;
	timezone: string;
};

export const TermsAndConditions: FC<TermsAndConditionsProps> = ({
	subscriptionTermsAndConditionsAcceptance = [],
	achAuthorizationAcceptances = [],
	hasAcceptedMostRecentTerms,
	setShowDialog,
	timezone,
}) => {
	const rows = useMemo(() => {
		let currentVersion = 0;
		let lastHistoryId: string | null = null;

		const temp = subscriptionTermsAndConditionsAcceptance.map((item, index) => {
			const historyId = item.termsAndConditionsHistory?.id ?? null;

			if (historyId !== lastHistoryId) {
				currentVersion += 1;
				lastHistoryId = historyId;
			}

			return {
				id: `${item.acceptanceDate || 'acceptance'}-${index}`,
				...item,
				versionGroup: currentVersion,
			};
		});

		const maxVersion = currentVersion;

		return temp.map((row) => ({
			...row,
			version: maxVersion - row.versionGroup + 1,
		}));
	}, [subscriptionTermsAndConditionsAcceptance]);

	const latestAcceptance = rows.length ? rows[0] : null;

	const { user = {}, acceptanceDate, methodOfAcceptance, version } = latestAcceptance ?? {};

	const { firstName = '', lastName = '', email = '' } = user ?? {};

	const acceptedBy = firstName ? `${firstName} ${lastName} - ${email}` : 'N/A';

	const formattedAcceptanceDate = (() => {
		if (!acceptanceDate) return 'N/A';

		const parsed = dayjs.utc(acceptanceDate).tz(timezone);

		return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm A z') : 'N/A';
	})();

	const latestAchAcceptance = achAuthorizationAcceptances[0];
	const latestAchAcceptedBy = latestAchAcceptance?.user
		? `${latestAchAcceptance.user.firstName || ''} ${latestAchAcceptance.user.lastName || ''}`.trim() ||
			latestAchAcceptance.user.email ||
			'N/A'
		: 'N/A';
	const latestAchAcceptedAt = (() => {
		if (!latestAchAcceptance?.acceptedAt) {
			return 'N/A';
		}

		const parsed = dayjs.utc(latestAchAcceptance?.acceptedAt).tz(timezone);
		return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm A z') : 'N/A';
	})();
	const latestAchEffectiveDate = (() => {
		if (!latestAchAcceptance?.effectiveDate) {
			return 'N/A';
		}

		const parsed = dayjs.utc(latestAchAcceptance?.effectiveDate);
		return parsed.isValid() ? parsed.format('MM/DD/YYYY') : 'N/A';
	})();
	const latestAchAmount = (() => {
		const amount = Number(latestAchAcceptance?.amount);
		return Number.isFinite(amount) ? amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A';
	})();

	const showAchAuthorizationDetails = (acceptance: AchAuthorizationAcceptanceRow) => {
		setShowDialog?.(
			<CenteredDismissModal
				title='ACH Authorization Details'
				content={
					<div style={{ maxHeight: '500px', overflowY: 'auto' }}>
						<p>
							<strong>Operator:</strong> {acceptance.operatorLegalName || 'N/A'}
						</p>
						<p>
							<strong>Operator account:</strong> ****{acceptance.operatorAccountLast4 || 'N/A'}
						</p>
						<p>
							<strong>Operator ABA:</strong> ****{acceptance.operatorAbaLast4 || 'N/A'}
						</p>
						<p>
							<strong>Frequency:</strong> {acceptance.recurrenceFrequency || 'One time'}
						</p>
						<p>
							<strong>Revocation notice:</strong> {acceptance.revocationNoticeDays ?? 'N/A'} days
						</p>
						<p>
							<strong>Cancellation instructions:</strong> {acceptance.cancellationMethod || 'N/A'}
						</p>
						<p>
							<strong>User agent:</strong> {acceptance.userAgent || 'N/A'}
						</p>
						<p>
							<strong>Authorization text:</strong>
						</p>
						<p style={{ whiteSpace: 'pre-wrap' }}>{acceptance.authorizationText || 'N/A'}</p>
					</div>
				}
				dismissText='Close'
				handleDismiss={() => setShowDialog(null)}
				width='700px'
			/>,
		);
	};

	const columns: GridColDef[] = [
		{
			field: 'version',
			headerName: 'Version',
			flex: 1,
			minWidth: 120,
		},
		{
			field: 'acceptanceDate',
			headerName: 'Acceptance Date',
			flex: 1.2,
			minWidth: 180,
			valueGetter: adaptGridValueCallback((params) => {
				const date = params.row?.acceptanceDate;
				if (!date) return 'N/A';

				const parsed = dayjs.utc(date).tz(timezone);
				return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm A z') : 'N/A';
			}),
		},
		{
			field: 'methodOfAcceptance',
			headerName: 'Method of Acceptance',
			flex: 1.2,
			minWidth: 180,
			valueGetter: adaptGridValueCallback(
				(params) => MethodOfAcceptingTermsAndConditions[params.row?.methodOfAcceptance] || 'N/A',
			),
		},
		{
			field: 'acceptedBy',
			headerName: 'Accepted By',
			flex: 1.2,
			minWidth: 180,
			valueGetter: adaptGridValueCallback((params) => {
				const first = params.row?.user?.firstName || '';
				const last = params.row?.user?.lastName || '';
				const fullName = `${first} ${last}`.trim();
				return fullName || 'N/A';
			}),
		},
		{
			field: 'userIp',
			headerName: 'IP Address',
			flex: 1.4,
			minWidth: 220,
			valueGetter: adaptGridValueCallback((params) => params.row?.userIp || 'N/A'),
		},
		{
			field: 'email',
			headerName: 'Email',
			flex: 1.4,
			minWidth: 220,
			valueGetter: adaptGridValueCallback((params) => params.row?.user?.email || 'N/A'),
		},
		{
			field: 'termsAndConditions',
			headerName: 'Terms & Conditions',
			sortable: false,
			filterable: false,
			width: 175,
			renderCell: (params) => (
				<span
					style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
					onClick={() =>
						setShowDialog(
							<CenteredDismissModal
								title='Terms & Conditions Details'
								content={
									<div style={{ maxHeight: '300px', overflowY: 'auto' }}>
										<p>
											<strong>Summary:</strong>{' '}
											{params.row?.termsAndConditionsHistory?.summary || 'N/A'}
										</p>
										<p>
											<strong>Full Text:</strong>{' '}
											{params.row?.termsAndConditionsHistory?.text || 'N/A'}
										</p>
									</div>
								}
								dismissText='Close'
								handleDismiss={() => setShowDialog(null)}
								width='500px'
								titleStyle={{ fontWeight: 'bold', textAlign: 'center' }}
							/>,
						)
					}
				>
					View
				</span>
			),
		},
	];

	const achColumns: GridColDef[] = [
		{
			field: 'acceptedAt',
			headerName: 'Accepted At',
			minWidth: 190,
			flex: 1,
			valueGetter: adaptGridValueCallback((params) => {
				const parsed = dayjs.utc(params.row?.acceptedAt).tz(timezone);
				return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm A z') : 'N/A';
			}),
		},
		{
			field: 'authorizationType',
			headerName: 'Authorization Type',
			minWidth: 165,
			flex: 1,
			valueGetter: adaptGridValueCallback((params) => formatLabel(params.row?.authorizationType)),
		},
		{
			field: 'methodOfAcceptance',
			headerName: 'Accepted Through',
			minWidth: 165,
			flex: 1,
			valueGetter: adaptGridValueCallback((params) => formatLabel(params.row?.methodOfAcceptance)),
		},
		{
			field: 'acceptedBy',
			headerName: 'Accepted By',
			minWidth: 190,
			flex: 1,
			valueGetter: adaptGridValueCallback((params) => {
				const name = `${params.row?.user?.firstName || ''} ${params.row?.user?.lastName || ''}`.trim();
				return name || params.row?.user?.email || 'N/A';
			}),
		},
		{
			field: 'accountType',
			headerName: 'Account Type',
			minWidth: 135,
			flex: 0.8,
			valueGetter: adaptGridValueCallback((params) => formatLabel(params.row?.accountType)),
		},
		{
			field: 'amount',
			headerName: 'Amount',
			minWidth: 110,
			valueGetter: adaptGridValueCallback((params) => {
				const amount = Number(params.row?.amount);
				return Number.isFinite(amount)
					? amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
					: 'N/A';
			}),
		},
		{
			field: 'effectiveDate',
			headerName: 'Effective Date',
			minWidth: 140,
			valueGetter: adaptGridValueCallback((params) => {
				const parsed = dayjs.utc(params.row?.effectiveDate);
				return parsed.isValid() ? parsed.format('MM/DD/YYYY') : 'N/A';
			}),
		},
		{ field: 'userIp', headerName: 'IP Address', minWidth: 170, flex: 1 },
		{
			field: 'details',
			headerName: 'Authorization',
			width: 145,
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<span
					style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
					onClick={() => showAchAuthorizationDetails(params.row)}
				>
					View
				</span>
			),
		},
	];

	return (
		<Grid container direction='column' gap={3}>
			<Grid container direction='row' gap={2} flexWrap='wrap'>
				<Grid
					className='subscriptionInfoGrid'
					container
					sx={{
						flexDirection: 'column',
						marginLeft: '0px',
						width: '30%',
						minWidth: '350px',
						p: 2,
						borderRadius: 2,
						backgroundColor: 'grey.100',
					}}
				>
					<Typography variant='h6' gutterBottom marginBottom='15px'>
						Terms & Conditions Acceptance
					</Typography>

					<Grid item>
						<span style={{ color: '#555' }}>
							Accepted on <strong>{formattedAcceptanceDate}</strong>
						</span>
					</Grid>

					<Grid item>
						<span style={{ color: '#555' }}>
							Accepted By: <strong>{acceptedBy}</strong>
						</span>
					</Grid>

					<Grid item>
						<span style={{ color: '#555' }}>
							Method of Acceptance:{' '}
							<strong>{MethodOfAcceptingTermsAndConditions[methodOfAcceptance] || 'N/A'}</strong>
						</span>
					</Grid>

					{latestAcceptance && (
						<Grid item>
							<span style={{ color: '#555' }}>
								Version: <strong>{version}</strong>
							</span>
						</Grid>
					)}

					{latestAcceptance && (
						<Grid item>
							<span style={{ color: '#555' }}>
								Has accepted most recent terms:{' '}
								<strong>{hasAcceptedMostRecentTerms ? 'Yes' : 'No'}</strong>
							</span>
						</Grid>
					)}
				</Grid>

				<Grid
					className='subscriptionInfoGrid'
					container
					sx={{
						flexDirection: 'column',
						width: '30%',
						minWidth: '350px',
						p: 2,
						borderRadius: 2,
						backgroundColor: 'grey.100',
					}}
				>
					<Typography variant='h6' gutterBottom marginBottom='15px'>
						ACH Authorization Acceptance
					</Typography>
					<Grid item>
						<span style={{ color: '#555' }}>
							Accepted on <strong>{latestAchAcceptedAt}</strong>
						</span>
					</Grid>
					<Grid item>
						<span style={{ color: '#555' }}>
							Accepted By: <strong>{latestAchAcceptedBy}</strong>
						</span>
					</Grid>
					<Grid item>
						<span style={{ color: '#555' }}>
							Authorization Type: <strong>{formatLabel(latestAchAcceptance?.authorizationType)}</strong>
						</span>
					</Grid>
					<Grid item>
						<span style={{ color: '#555' }}>
							Accepted Through: <strong>{formatLabel(latestAchAcceptance?.methodOfAcceptance)}</strong>
						</span>
					</Grid>
					<Grid item>
						<span style={{ color: '#555' }}>
							Account Type: <strong>{formatLabel(latestAchAcceptance?.accountType)}</strong>
						</span>
					</Grid>
					<Grid item>
						<span style={{ color: '#555' }}>
							Amount: <strong>{latestAchAmount}</strong>
						</span>
					</Grid>
					<Grid item>
						<span style={{ color: '#555' }}>
							Effective Date: <strong>{latestAchEffectiveDate}</strong>
						</span>
					</Grid>
					{latestAchAcceptance && (
						<Grid item>
							<span
								style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
								onClick={() => showAchAuthorizationDetails(latestAchAcceptance)}
							>
								View Authorization
							</span>
						</Grid>
					)}
				</Grid>
			</Grid>

			{rows.length > 0 && (
				<div data-cy='terms-and-conditions-history-table-container'>
					<Typography variant='h6' gutterBottom>
						Terms & Conditions Acceptance History
					</Typography>

					<DataGridPremium
						autoHeight
						rows={rows}
						columns={columns}
						disableRowSelectionOnClick
						hideFooter
						getRowId={(row) => row.id}
						sx={{ backgroundColor: 'white' }}
					/>
				</div>
			)}

			{achAuthorizationAcceptances.length > 0 && (
				<div data-cy='ach-authorization-acceptance-table-container'>
					<Typography variant='h6' gutterBottom>
						ACH Authorization Acceptances
					</Typography>
					<DataGridPremium
						autoHeight
						rows={achAuthorizationAcceptances}
						columns={achColumns}
						disableRowSelectionOnClick
						hideFooter={achAuthorizationAcceptances.length <= 100}
						getRowId={(row) => row.id}
						sx={{ backgroundColor: 'white' }}
					/>
				</div>
			)}
		</Grid>
	);
};
